import styled from "styled-components";
import { useMemo } from "react";

import { Asset } from "./Asset";

import {
  BrandAssetGroup,
  BrandAssetWidth,
  CompanyFragment,
  CompanyProfileLinkFragment,
} from "@otta/search/schema";
import { media } from "@otta/design";

const FlexDiv = styled.div`
  flex-wrap: wrap;
  display: flex;
  gap: 1rem;
`;

const Assets = styled.div`
  grid-template-columns: repeat(6, 1fr);
  display: grid;
  margin: auto;
  gap: 1rem;
`;

/**
 * Some hacky shennanigans to make one-third images 6 grid cells
 * if they're the only asset on desktop, so they fit to the side of the content.
 */
function desktopWidth({ width }: { width: BrandAssetWidth }): string {
  return (
    `grid-column-end: span ${gridWidth(width)};` +
    (width === BrandAssetWidth.OneThird
      ? "&:only-child { grid-column-end: span 6; }"
      : "")
  );
}

const StyledAsset = styled(Asset)`
  ${({ asset: { mobilePlacement: mobile } }) =>
    mobile && `grid-column-end: span ${gridWidth(mobile.width)};`}
  ${({ asset: { desktopPlacement: desktop } }) =>
    desktop && media({ from: "tablet" })`${desktopWidth(desktop)}`}
`;

const Content = styled.div`
  margin-bottom: 1rem;
  flex-basis: 60%;
  flex-grow: 1;
`;

function gridWidth(width: BrandAssetWidth): number {
  switch (width) {
    case BrandAssetWidth.OneThird:
      return 2;
    case BrandAssetWidth.OneHalf:
      return 3;
    case BrandAssetWidth.TwoThirds:
      return 4;
    case BrandAssetWidth.Full:
      return 6;
  }
}

/**
 * Wraps some content and adds assets around it such that
 * a portrait picture will usually go beside the content
 * but landscape pictures / multiple pictures etc go after
 */
export function AssetGroupSprinkler({
  "data-testid": testId,
  children,
  assets,
  group,
}: React.PropsWithChildren<{
  "data-testid"?: string;
  group?: BrandAssetGroup;
  assets?: CompanyFragment["brandAssetGroups"];
}>): React.ReactElement {
  const found: CompanyProfileLinkFragment[] = useMemo(
    () => assets?.find(a => a.group.name === group)?.links ?? [],
    [assets, group]
  );
  return (
    <AssetSprinkler assets={found} data-testid={testId}>
      {children}
    </AssetSprinkler>
  );
}

export function AssetSprinkler({
  "data-testid": testId,
  children,
  assets,
}: React.PropsWithChildren<{
  "data-testid"?: string;
  assets?: CompanyProfileLinkFragment[];
}>): React.ReactElement {
  if (!assets?.length) {
    return <div data-testid={testId}>{children}</div>;
  }

  return (
    <FlexDiv data-testid={testId}>
      <Content>{children}</Content>
      <Assets>
        {assets.map(
          ba => !!ba.mobilePlacement && <StyledAsset asset={ba} key={ba.id} />
        )}
      </Assets>
    </FlexDiv>
  );
}
